import { LitElement, html, nothing, type CSSResultGroup, type PropertyValues } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { panelStyles, accordionStyles } from './styles.js';
import { emit } from '../utils/dom.utils.js';

// Internal event name — panels dispatch it, the accordion intercepts it.
const TOGGLE_EVENT = 'uwc-accordion-toggle';

export type AccordionToggleIconPos = 'start' | 'end';

// ══════════════════════════════════════════════════════════════════════════════
// uwc-accordion-panel
//
// Built on the native <details> / <summary> elements for W3C-standard
// semantics. The browser provides:
//   • focusable <summary> with role="button" behaviour
//   • aria-expanded automatically wired to details[open]
//   • keyboard activation (Enter / Space) out of the box
//
// Expand / collapse animation uses the Web Animations API (WAAPI) to
// interpolate between measured pixel heights. This mirrors the Shoelace
// sl-details approach and produces smooth, interruptible transitions.
// ══════════════════════════════════════════════════════════════════════════════

/**
 * uwc-accordion-panel
 *
 * A collapsible disclosure panel backed by `<details>`/`<summary>`.
 *
 * @attr header           - Header text (overridden by `slot="header"`).
 * @attr collapsed        - Reflects: panel is collapsed. Default: true.
 * @attr disabled         - Prevent toggling.
 * @attr value            - Optional unique identifier used in events.
 * @attr toggle-icon      - Bootstrap Icon name for the toggle indicator.
 *                          Rotates 90° when open. Default: `"chevron-right"`.
 * @attr toggle-icon-pos  - Position of the toggle icon within the header:
 *                          `"start"` (left) | `"end"` (right). Default: `"end"`.
 *
 * @slot default - Collapsible body content.
 * @slot header  - Custom header content (replaces the `header` attribute text).
 * @slot icon    - Fully replace the built-in `<uwc-icon>` toggle indicator.
 *                 The wrapper `<span>` still applies the active colour and the
 *                 90° rotation transition.
 *
 * @fires uwc-accordion-toggle - Internal: bubbles to parent `uwc-accordion`.
 * @fires uwc-open             - Fired when this panel opens (standalone mode).
 * @fires uwc-close            - Fired when this panel closes (standalone mode).
 *
 * @csspart panel       - The `<details>` element.
 * @csspart header      - The `<summary>` element.
 * @csspart title       - The title `<span>` inside the header.
 * @csspart toggle-icon - The icon wrapper `<span>`.
 * @csspart content     - The body `<div>`.
 *
 * @cssprop --uwc-acc-panel-bg              - Panel background.
 * @cssprop --uwc-acc-panel-border          - Panel border.
 * @cssprop --uwc-acc-panel-radius          - Panel border radius.
 * @cssprop --uwc-acc-header-padding        - Header padding.
 * @cssprop --uwc-acc-header-bg             - Header background (resting).
 * @cssprop --uwc-acc-header-hover-bg       - Header background on hover.
 * @cssprop --uwc-acc-header-active-bg      - Header background when open.
 * @cssprop --uwc-acc-header-color          - Header text colour (resting).
 * @cssprop --uwc-acc-header-active-color   - Header text colour when open.
 * @cssprop --uwc-acc-header-font-size      - Header font size.
 * @cssprop --uwc-acc-header-font-weight    - Header font weight.
 * @cssprop --uwc-acc-icon-color            - Toggle icon colour (resting).
 * @cssprop --uwc-acc-icon-active-color     - Toggle icon colour when open.
 * @cssprop --uwc-acc-content-padding       - Body padding.
 * @cssprop --uwc-acc-content-color         - Body text colour.
 * @cssprop --uwc-acc-content-border        - Separator between header and body.
 * @cssprop --uwc-acc-content-font-size     - Body font size.
 */
export class UwcAccordionPanel extends LitElement {
  static override styles: CSSResultGroup = panelStyles;

  // ── Public properties ─────────────────────────────────────────────────────

  /** Header text shown when no `header` slot content is provided. */
  @property() header = '';

  /** Whether the panel is collapsed (closed). Reflects to attribute. Default: true. */
  @property({ type: Boolean, reflect: true }) collapsed = true;

  /** Prevent the user from opening or closing this panel. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Optional unique identifier used in parent-accordion event payloads. */
  @property() value = '';

  /**
   * Bootstrap Icons name for the toggle indicator.
   * The icon rotates 90° via CSS transition when the panel opens, so
   * `chevron-right` naturally becomes `chevron-down` in the open state.
   *
   * @default "chevron-right"
   */
  @property({ attribute: 'toggle-icon' }) toggleIcon = 'chevron-right';

  /**
   * Where the toggle icon sits within the header row.
   * - `"end"`   (default) — trailing edge.
   * - `"start"` — leading edge, useful for tree/outline layouts.
   *
   * Reflects to attribute so `:host([toggle-icon-pos="start"])` works in CSS.
   */
  @property({ attribute: 'toggle-icon-pos', reflect: true })
  toggleIconPos: AccordionToggleIconPos = 'end';

  // ── Shadow DOM refs ───────────────────────────────────────────────────────

  @query('details') private _details?: HTMLDetailsElement;
  @query('.uwc-acc-panel__content-wrap') private _wrap?: HTMLElement;

  // ── In-flight WAAPI animation (allows clean interruption) ─────────────────
  private _currentAnimation?: Animation;

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  override connectedCallback() {
    super.connectedCallback();
    this.dispatchEvent(new CustomEvent('uwc-accordion-panel-connected', {
      bubbles:  true,
      composed: true,
    }));
  }

  override firstUpdated() {
    // Sync the native <details> open state with the initial `collapsed` prop
    // without animation. The guard in updated() (open === shouldBeOpen) ensures
    // this direct assignment does not trigger _animateOpen / _animateClose.
    if (this._details) {
      this._details.open = !this.collapsed;
    }
  }

  override updated(changed: PropertyValues) {
    super.updated(changed);
    if (!changed.has('collapsed') || !this._details) return;

    const shouldBeOpen = !this.collapsed;
    // Guard: already in the correct state — skip (covers firstUpdated sync)
    if (this._details.open === shouldBeOpen) return;

    if (shouldBeOpen) {
      this._animateOpen();
    } else {
      this._animateClose();
    }
  }

  // ── WAAPI animation helpers ───────────────────────────────────────────────

  /**
   * Expand the panel using WAAPI.
   *
   * Strategy (mirrors Shoelace sl-details):
   *   1. Capture the current rendered height before cancelling any in-flight
   *      animation — this gives a smooth reversal when rapidly toggling.
   *   2. Cancel the previous animation (removes its fill effect).
   *   3. Set details.open = true so the UA stylesheet makes content visible
   *      and scrollHeight becomes measurable.
   *   4. Animate height from `fromHeight` → `scrollHeight`.
   *   5. On finish, cancel the animation (releases fill) and clear inline styles.
   */
  private _animateOpen() {
    const details = this._details;
    const wrap    = this._wrap;
    if (!details || !wrap) return;

    // Capture mid-animation height before cancelling for smooth reversal
    const fromHeight = this._currentAnimation
      ? wrap.getBoundingClientRect().height
      : 0;

    this._currentAnimation?.cancel();
    this._currentAnimation = undefined;

    // Make content visible so scrollHeight is measurable
    details.open = true;
    const toHeight = wrap.scrollHeight;

    const anim = wrap.animate(
      [
        { height: `${fromHeight}px`, overflow: 'hidden' },
        { height: `${toHeight}px`,   overflow: 'hidden' },
      ],
      {
        duration: this._duration(),
        easing:   'ease',
        fill:     'both',
      },
    );
    this._currentAnimation = anim;

    anim.addEventListener('finish', () => {
      if (this._currentAnimation !== anim) return;
      // Cancel fill so CSS takes over (height: auto)
      anim.cancel();
      wrap.style.removeProperty('height');
      wrap.style.removeProperty('overflow');
      this._currentAnimation = undefined;
    }, { once: true });
  }

  /**
   * Collapse the panel using WAAPI.
   *
   * Strategy:
   *   1. Capture current rendered height (handles mid-animation state).
   *   2. Cancel any in-flight animation.
   *   3. Animate height → 0.
   *   4. On finish, set details.open = false (UA hides content) and clear styles.
   */
  private _animateClose() {
    const details = this._details;
    const wrap    = this._wrap;

    if (!details?.open || !wrap) {
      if (details) details.open = false;
      return;
    }

    const fromHeight = wrap.getBoundingClientRect().height;

    this._currentAnimation?.cancel();
    this._currentAnimation = undefined;

    const anim = wrap.animate(
      [
        { height: `${fromHeight}px`, overflow: 'hidden' },
        { height: '0px',             overflow: 'hidden' },
      ],
      {
        duration: this._duration(),
        easing:   'ease',
        fill:     'both',
      },
    );
    this._currentAnimation = anim;

    anim.addEventListener('finish', () => {
      if (this._currentAnimation !== anim) return;
      anim.cancel();
      details.open = false;
      wrap.style.removeProperty('height');
      wrap.style.removeProperty('overflow');
      this._currentAnimation = undefined;
    }, { once: true });
  }

  /** Returns 0ms when the user prefers reduced motion, otherwise 250ms. */
  private _duration(): number {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 250;
  }

  // ── Summary event handlers ────────────────────────────────────────────────

  private _onSummaryClick(e: MouseEvent) {
    // Prevent the browser's built-in <details> toggle — we control details.open
    // ourselves so the WAAPI animation runs on both open and close.
    e.preventDefault();
    if (this.disabled) return;

    const evt = new CustomEvent(TOGGLE_EVENT, {
      bubbles:    true,
      composed:   true,
      cancelable: true,
    });
    this.dispatchEvent(evt);

    if (!evt.defaultPrevented) {
      // Standalone (no accordion parent): toggle directly
      this._selfToggle();
    }
  }

  private _selfToggle() {
    if (this.collapsed) {
      this.collapsed = false;
      emit(this, 'uwc-open');
    } else {
      this.collapsed = true;
      emit(this, 'uwc-close');
    }
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /** Open this panel. No-op if already open or disabled. */
  open() {
    if (this.disabled || !this.collapsed) return;
    this.collapsed = false;
    emit(this, 'uwc-open');
  }

  /** Close this panel. No-op if already closed or disabled. */
  close() {
    if (this.disabled || this.collapsed) return;
    this.collapsed = true;
    emit(this, 'uwc-close');
  }

  /** Toggle this panel. */
  toggle() {
    this.collapsed ? this.open() : this.close();
  }

  // ── Private render helpers ────────────────────────────────────────────────

  /**
   * Renders the `<uwc-icon>` toggle indicator inside a wrapper `<span>`.
   * The wrapper drives a 90° CSS rotation via `details[open]` selector so
   * `chevron-right` (closed) smoothly rotates to point downward (open).
   *
   * `aria-hidden` keeps it out of the AT tree — `<summary>` already carries
   * the expanded/collapsed state.
   */
  private _renderToggleIcon() {
    return html`
      <span part="toggle-icon" class="uwc-acc-panel__chevron" aria-hidden="true">
        <slot name="icon">
          <uwc-icon name=${this.toggleIcon}></uwc-icon>
        </slot>
      </span>
    `;
  }

  // ── Render ────────────────────────────────────────────────────────────────

  protected override render() {
    const atStart = this.toggleIconPos === 'start';

    return html`
      <details part="panel">
        <summary
          part="header"
          class=${classMap({
            'uwc-acc-panel__header':           true,
            'uwc-acc-panel__header--disabled': this.disabled,
          })}
          aria-disabled=${this.disabled ? 'true' : nothing}
          @click=${this._onSummaryClick}
        >
          ${atStart ? this._renderToggleIcon() : nothing}

          <slot name="header">
            <span part="title" class="uwc-acc-panel__title">${this.header}</span>
          </slot>

          ${!atStart ? this._renderToggleIcon() : nothing}
        </summary>

        <div class="uwc-acc-panel__content-wrap">
          <div part="content" class="uwc-acc-panel__content">
            <slot></slot>
          </div>
        </div>
      </details>
    `;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// uwc-accordion
// ══════════════════════════════════════════════════════════════════════════════

/**
 * uwc-accordion
 *
 * Coordinates a set of `uwc-accordion-panel` children. In single mode
 * (default) opening one panel closes any previously open panel. Set `multiple`
 * to allow any number of panels open simultaneously.
 *
 * Each panel is a native `<details>`/`<summary>` element; the accordion merely
 * orchestrates which ones are open.
 *
 * @attr multiple     - Allow more than one panel open simultaneously.
 * @attr stacked      - Remove gap; panels share borders (flush layout).
 * @attr active-index - Comma-separated zero-based indices to open on first render.
 *                      E.g. `"0"` or `"0,2"` (combine with `multiple`).
 *
 * @slot default - One or more `uwc-accordion-panel` elements.
 *
 * @fires uwc-open   - A panel opened.   `detail: { index, value }`.
 * @fires uwc-close  - A panel closed.   `detail: { index, value }`.
 * @fires uwc-change - Any state change. `detail: { activeIndex: number[] }`.
 *
 * @cssprop --uwc-acc-gap - Gap between panels. Default: 0.5rem.
 */
export class UwcAccordion extends LitElement {
  static override styles: CSSResultGroup = accordionStyles;

  // ── Public properties ─────────────────────────────────────────────────────

  /** Allow multiple panels open at the same time. */
  @property({ type: Boolean, reflect: true }) multiple = false;

  /** Stack panels flush — no gap, shared borders, rounded corners only on ends. */
  @property({ type: Boolean, reflect: true }) stacked = false;

  /**
   * Comma-separated panel indices to pre-open on first render, e.g. `"0"` or
   * `"0,2"`. Parsed once; use `openPanel()` / `closePanel()` for later changes.
   */
  @property({ attribute: 'active-index' }) activeIndex = '';

  // ── Internal state ────────────────────────────────────────────────────────

  private _panels: UwcAccordionPanel[] = [];

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  override connectedCallback() {
    super.connectedCallback();
    this.addEventListener(TOGGLE_EVENT,                    this._onPanelToggle  as EventListener);
    this.addEventListener('uwc-accordion-panel-connected', this._onPanelConnect as EventListener);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(TOGGLE_EVENT,                    this._onPanelToggle  as EventListener);
    this.removeEventListener('uwc-accordion-panel-connected', this._onPanelConnect as EventListener);
  }

  override firstUpdated() {
    this._syncPanels();
    this._applyGrouped();
    this._applyActiveIndex();
  }

  override updated(changed: PropertyValues) {
    super.updated(changed);
    if (changed.has('stacked')) this._applyGrouped();
  }

  // ── Internal helpers ──────────────────────────────────────────────────────

  private _syncPanels() {
    this._panels = Array.from(this.children).filter(
      (el): el is UwcAccordionPanel => el instanceof UwcAccordionPanel,
    );
  }

  private _applyGrouped() {
    Array.from(this.children)
      .filter((el): el is UwcAccordionPanel => el instanceof UwcAccordionPanel)
      .forEach(p => {
        if (this.stacked) p.setAttribute('grouped', '');
        else              p.removeAttribute('grouped');
      });
  }

  private _applyActiveIndex() {
    if (!this.activeIndex) return;
    this.activeIndex
      .split(',')
      .map(s => parseInt(s.trim(), 10))
      .filter(n => !isNaN(n))
      .forEach(i => {
        const p = this._panels[i];
        if (p && !p.disabled) p.collapsed = false;
      });
  }

  // ── Event handlers ────────────────────────────────────────────────────────

  private _onPanelConnect() {
    this._syncPanels();
    this._applyGrouped();
  }

  private _onPanelToggle(e: Event) {
    const panel = e.target as UwcAccordionPanel;
    const index = this._panels.indexOf(panel);
    if (index === -1) return;

    (e as CustomEvent).preventDefault();

    const wasCollapsed = panel.collapsed;

    if (!this.multiple) {
      this._panels.forEach((p, i) => {
        if (p !== panel && !p.collapsed) {
          p.collapsed = true;
          emit(this, 'uwc-close', { index: i, value: p.value || String(i) });
        }
      });
    }

    if (wasCollapsed) {
      panel.collapsed = false;
      emit(this, 'uwc-open',  { index, value: panel.value || String(index) });
    } else {
      panel.collapsed = true;
      emit(this, 'uwc-close', { index, value: panel.value || String(index) });
    }

    this._emitChange();
  }

  private _onSlotChange() {
    this._syncPanels();
    this._applyGrouped();
  }

  private _emitChange() {
    emit(this, 'uwc-change', { activeIndex: this.getActiveIndices() });
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /** Open the panel at `index`. In single mode, closes any currently open panel. */
  openPanel(index: number) {
    const panel = this._panels[index];
    if (!panel || panel.disabled || !panel.collapsed) return;

    if (!this.multiple) {
      this._panels.forEach((p, i) => {
        if (p !== panel && !p.collapsed) {
          p.collapsed = true;
          emit(this, 'uwc-close', { index: i, value: p.value || String(i) });
        }
      });
    }

    panel.collapsed = false;
    emit(this, 'uwc-open', { index, value: panel.value || String(index) });
    this._emitChange();
  }

  /** Close the panel at `index`. */
  closePanel(index: number) {
    const panel = this._panels[index];
    if (!panel || panel.collapsed) return;
    panel.collapsed = true;
    emit(this, 'uwc-close', { index, value: panel.value || String(index) });
    this._emitChange();
  }

  /** Open all non-disabled panels. Most useful with `multiple`. */
  openAll() {
    this._panels.forEach((p, i) => {
      if (!p.disabled && p.collapsed) {
        p.collapsed = false;
        emit(this, 'uwc-open', { index: i, value: p.value || String(i) });
      }
    });
    this._emitChange();
  }

  /** Close all panels. */
  closeAll() {
    this._panels.forEach((p, i) => {
      if (!p.collapsed) {
        p.collapsed = true;
        emit(this, 'uwc-close', { index: i, value: p.value || String(i) });
      }
    });
    this._emitChange();
  }

  /** Returns the indices of all currently open panels. */
  getActiveIndices(): number[] {
    return this._panels.map((p, i) => (p.collapsed ? -1 : i)).filter(i => i >= 0);
  }

  // ── Render ────────────────────────────────────────────────────────────────

  protected override render() {
    return html`<slot @slotchange=${this._onSlotChange}></slot>`;
  }
}

// ── Global type augmentation ──────────────────────────────────────────────────

declare global {
  interface HTMLElementTagNameMap {
    'uwc-accordion':       UwcAccordion;
    'uwc-accordion-panel': UwcAccordionPanel;
  }
}
